import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useAuthStore } from '../../stores/auth.store';
import { useModalStore } from '../../stores/modal.store';
import { toast } from '../../stores/toast.store';
import { Icon } from '../../components/ui/Icon';
import { Chip } from '../../components/ui/Chip';
import { AnimatedNumber } from '../../components/ui/AnimatedNumber';
import { ActionsMenu } from '../../components/ui/ActionsMenu';
import { EmptyState } from '../../components/ui/EmptyState';
import { SkeletonKPIRow, SkeletonTable } from '../../components/ui/Skeleton';
// Telefono: i filtri finiscono dietro un solo tasto. Sul computer questo
// componente restituisce i suoi figli così come sono, quindi qui non cambia niente.
import { MobileFilters } from '../../components/ui/MobileFilters';
import { DesktopOnly } from '../../components/ui/DesktopOnly';
import { fmtDate } from '../../lib/utils';
import { ModuleKey, hasAtLeast, PermissionLevel } from '@masamune/shared';
import './vault.css';

interface VaultEntry {
  id: string; label: string; category: string; username: string | null; url: string | null;
  owner: { firstName: string; lastName: string } | null;
  allowedRoleIds: string[]; lastAccessAt: string | null;
  expiresAt: string | null; createdAt: string;
}

export function VaultPage() {
  const user = useAuthStore((s) => s.user);
  const openModal = useModalStore((s) => s.open);
  const [revealed, setRevealed] = useState<Record<string, string | undefined>>({});
  const [search, setSearch] = useState('');
  // KPI cliccabile (pattern P4): mostra solo le voci con una scadenza impostata
  const [expOnly, setExpOnly] = useState(false);

  const canAccess = user && hasAtLeast(
    user.permissions.find((p) => p.module === ModuleKey.VAULT)?.level ?? PermissionLevel.NONE,
    PermissionLevel.READ,
  );

  const { data, isLoading } = useQuery({
    queryKey: ['vault', search],
    queryFn: () => api.get<{ items: VaultEntry[] }>(`/vault${search ? `?search=${encodeURIComponent(search)}` : ''}`),
    enabled: !!canAccess,
  });

  // Query separata senza filtro search: serve per le KPI in alto (totali, categorie, expiry, owner)
  // che devono restare stabili anche quando l'utente filtra la lista.
  const { data: allData } = useQuery({
    queryKey: ['vault', 'stats'],
    queryFn: () => api.get<{ items: VaultEntry[] }>('/vault'),
    enabled: !!canAccess,
  });

  const revealMut = useMutation({
    mutationFn: (id: string) => api.post<{ password: string }>(`/vault/${id}/reveal`),
    onSuccess: (res, id) => setRevealed((r) => ({ ...r, [id]: res.password })),
  });

  // Copia negli appunti SENZA mostrare a schermo: se non già rivelata, chiama lo
  // stesso endpoint /reveal (così l'accesso resta tracciato nel log) e copia.
  const copyPassword = async (id: string) => {
    try {
      const pwd = revealed[id] ?? (await api.post<{ password: string }>(`/vault/${id}/reveal`)).password;
      await navigator.clipboard.writeText(pwd);
      toast.ok('Password copiata', 'Negli appunti per pochi secondi.');
    } catch {
      toast.alert('Copia fallita', 'Browser non autorizza la clipboard.');
    }
  };

  const copyUsername = async (username: string) => {
    try {
      await navigator.clipboard.writeText(username);
      toast.ok('Username copiato');
    } catch {
      toast.alert('Copia fallita', 'Browser non autorizza la clipboard.');
    }
  };

  const items = (data?.items ?? []).filter((v) => !expOnly || !!v.expiresAt);

  if (!canAccess) {
    // Accesso negato: EmptyState standard al posto del placeholder ad-hoc
    return (
      <div className="page">
        <div className="max-w-[480px] mx-auto py-20">
          <EmptyState
            icon="lock"
            title="Password Vault"
            description={
              <>
                Accesso riservato a Super Admin e Admin Amministrazione.
                {user && <> Il tuo ruolo: <b>{user.role.name}</b>.</>}
              </>
            }
            action={
              <button
                className="btn"
                onClick={() => toast.info('Richiesta inviata', 'Un Super Admin riceverà la tua richiesta di accesso al Vault.')}
              >
                Richiedi accesso
              </button>
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      {/* ── RIFACIMENTO EDITORIALE: titolo grande e leggero, hairline aurora,
            numeri NUDI sul foglio (stat-band), tabella full-bleed ── */}
      <div className="page-ed-hd flex items-end justify-between gap-4">
        <div>
          <h1 className="ed-title">Password Vault</h1>
          <div className="ed-sub">Credenziali condivise, cifrate e con log accessi completo.</div>
        </div>
        {/* d-only: sul telefono l'azione principale non sta in cima, dove il
            pollice non arriva, ma nella barra in fondo alla pagina. */}
        <div className="flex gap-2 pb-1 d-only">
          <button className="btn btn-primary" onClick={() => openModal('newVault')}><Icon name="plus" /> Nuova voce</button>
          <ActionsMenu
            variant="ghost"
            items={[
              { key: 'export', label: 'Esporta audit log', icon: 'export', onClick: () => openModal('export', { what: 'Audit log' }) },
            ]}
          />
        </div>
      </div>

      {isLoading && !data ? (
        <SkeletonKPIRow count={4} />
      ) : (
      <div className="stat-band">
        <div>
          <div className="st-label">Credenziali totali</div>
          <div className="st-value"><AnimatedNumber value={allData?.items.length ?? 0} /></div>
        </div>
        <div>
          <div className="st-label">Categorie</div>
          <div className="st-value"><AnimatedNumber value={new Set((allData?.items ?? []).map((i) => i.category)).size} /></div>
        </div>
        {/* Metrica cliccabile (pattern P4): toggla il filtro "solo con scadenza" */}
        <div className="st-click" role="button" tabIndex={0} onClick={() => setExpOnly((v) => !v)} onKeyDown={(e) => { if (e.key === 'Enter') setExpOnly((v) => !v); }}>
          <div className="st-label">Con scadenza</div>
          <div className="st-value"><AnimatedNumber value={(allData?.items ?? []).filter((i) => i.expiresAt).length} /></div>
          <div className="st-delta">da rinnovare</div>
        </div>
        <div>
          <div className="st-label">Owner assegnati</div>
          <div className="st-value"><AnimatedNumber value={(allData?.items ?? []).filter((i) => i.owner).length} /></div>
        </div>
      </div>
      )}

      {/* mb-4 flex-wrap: è la forma canonica della barra filtri galleggiante
          (16px verso la tabella, e a schermo stretto i controlli vanno a capo
          invece di sbordare). Senza mb-4 il margine restava 0 e, collassando
          con .ed-section, la barra finiva a 4px dall'intestazione della tabella. */}
      <MobileFilters attivi={[search.trim(), expOnly ? '1' : ''].filter(Boolean).length}>
      <div className="filterbar filterbar--float mb-4 flex-wrap">
        {/* Le stesse misure di flex delle altre barre filtri dell'app: il
            width: 280 scritto qui prima non faceva niente, .tb-search ha
            flex: 1 e vinceva quello. */}
        <div className="tb-search" style={{ flex: '1 1 150px', minWidth: 150, maxWidth: 300, margin: 0 }}>
          <Icon name="search" size={12} />
          <input aria-label="Cerca nel vault" placeholder="Cerca voce, categoria, username…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        {expOnly && <Chip tone="warn"><Icon name="alert" size={10} /> Solo con scadenza</Chip>}
        {(search || expOnly) && (
          <button className="btn btn-sm btn-ghost" onClick={() => { setSearch(''); setExpOnly(false); }}>
            <Icon name="x" size={10} /> Reset
          </button>
        )}
        <div className="spacer" />
        <span className="mute text-[12px]">{items.length} {items.length === 1 ? 'risultato' : 'risultati'}</span>
      </div>
      </MobileFilters>

      {/* Full-bleed: la lista vive sul foglio, senza gabbia.
          tbl--card: sotto i 768px ogni riga diventa una scheda. Sul computer non
          fa niente. Le celle dicono con data-m che ruolo hanno nella scheda e con
          data-l come si chiamano. Regole: os-planning/mobile.md */}
      <div className="ed-section">
        <table className="tbl tbl--ed tbl--card">
          <thead>
            {/* Le larghezze sono dichiarate perché senza di loro l'username più
                lungo (un client id OAuth, una parola sola da 447px) si prendeva
                mezza tabella: la colonna Voce restava a 141px, andava a capo su
                quattro righe e la tabella sbordava di 144px a 1440. */}
            <tr>
              <th>Voce</th>
              <th style={{ width: 140 }}>Categoria</th>
              <th style={{ width: 150 }}>Username</th>
              <th style={{ width: 92 }}>Owner</th>
              {/* 168 e non 118: sotto questa misura l'intestazione "Ultimo accesso"
                  andava a capo ed era l'unica su due righe di tutta la riga. */}
              <th style={{ width: 168 }}>Ultimo accesso</th>
              <th style={{ width: 148 }}>Password</th>
              <th style={{ width: 116 }}>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && !data && <SkeletonTable rows={4} cols={7} withAvatar={false} />}
            {items.map((v) => {
              const shown = revealed[v.id];
              return (
                <tr key={v.id}>
                  <td data-m="hd" className="font-semibold">
                    {/* Una riga sola: i nomi delle voci vanno da dieci a cinquanta
                        caratteri e andando a capo facevano righe da 77 a 140px
                        nella stessa tabella. Sulla scheda del telefono il taglio
                        si spegne da solo (mobile.css lo fa per i data-m="hd"). */}
                    <div className="truncate" style={{ width: 200 }} title={v.label}>
                      {v.url ? (
                        <a href={v.url} target="_blank" rel="noreferrer" className="hover:underline vault-tap" title={v.url}>
                          {v.label} <Icon name="link" size={10} className="mute inline" />
                        </a>
                      ) : v.label}
                    </div>
                  </td>
                  <td data-m="key" data-l="Categoria"><span className="chip-label">{v.category.replace(/_/g, ' ')}</span></td>
                  <td data-m="key" data-l="Username">
                    {/* La larghezza sta sul contenitore e non sul bottone: un client
                        id OAuth è una parola sola da 447px e senza un limite vero
                        si prendeva 850px di tabella. Sulla scheda del telefono
                        mobile.css azzera la larghezza dei div[style] dentro le celle. */}
                    <div style={{ width: 110 }}>
                      {v.username ? (
                        <button
                          type="button"
                          className="mono hover:underline truncate block w-full text-left vault-user-btn vault-tap"
                          title={`${v.username} — clicca per copiare`}
                          onClick={() => copyUsername(v.username!)}
                        >
                          {v.username}
                        </button>
                      ) : <span className="mute-2">—</span>}
                    </div>
                  </td>
                  <td data-m="key" data-l="Owner">{v.owner ? `${v.owner.firstName[0]}. ${v.owner.lastName}` : <span className="mute-2">—</span>}</td>
                  <td data-m="key" data-l="Ultimo accesso" className="mono mute text-[12px]" style={{ whiteSpace: 'nowrap' }}>{fmtDate(v.lastAccessAt)}</td>
                  {/* Riga intera sulla scheda: è il dato per cui si apre questa pagina */}
                  <td data-m="row" data-l="Password" className={`mono ${shown ? 'text-text' : 'mute-2'}`} style={{ letterSpacing: shown ? 0 : '1.5px' }}>
                    {shown ?? '••••••••••••'}
                  </td>
                  {/* data-m="act": sulla scheda i due tasti si ancorano in alto a
                      destra. Serve anche a togliere il chevron "questa si apre",
                      che qui mentirebbe: una voce del vault non ha un dettaglio. */}
                  <td data-m="act">
                    <div className="flex gap-1">
                      <button
                        /* m-hit: solo sul telefono, allarga il bersaglio a 44px
                           con un riquadro invisibile. Sul computer non esiste
                           nessuna regola .m-hit, quindi il tasto resta identico. */
                        className="btn btn-sm btn-ghost m-hit"
                        aria-label={shown ? 'Nascondi password' : 'Rivela password'}
                        title={shown ? 'Nascondi password' : 'Rivela password'}
                        onClick={() => shown ? setRevealed((r) => ({ ...r, [v.id]: undefined })) : revealMut.mutate(v.id)}
                      >
                        <Icon name={shown ? 'eyeOff' : 'eye'} size={12} />
                      </button>
                      <button
                        className="btn btn-sm btn-ghost m-hit"
                        aria-label="Copia password (senza mostrarla)"
                        title="Copia password (senza mostrarla)"
                        onClick={() => copyPassword(v.id)}
                      >
                        <Icon name="file" size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {!items.length && (
              <tr>
                <td colSpan={7} style={{ padding: 0 }}>
                  <EmptyState
                    brand
                    title={search || expOnly ? 'Nessuna voce trovata' : 'Vault vuoto'}
                    description={
                      search || expOnly
                        ? 'Prova a modificare la ricerca o disattiva il filtro scadenza.'
                        : 'Salva qui le credenziali condivise (server, SaaS, integrazioni). Tutto cifrato e tracciato.'
                    }
                    action={!search && !expOnly ? (
                      <button className="btn btn-primary d-only" onClick={() => openModal('newVault')}>
                        <Icon name="plus" size={12} /> Salva la prima credenziale
                      </button>
                    ) : undefined}
                  />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Telefono: l'azione principale della schermata, appoggiata sopra la
          barra di navigazione. m-only la spegne sul computer, dove la stessa
          azione sta già in cima. */}
      <DesktopOnly
        cosa="La creazione di una nuova credenziale"
        perche="password, ruoli autorizzati e dati di sicurezza vanno verificati dal computer; dal telefono puoi consultare le voci esistenti."
      >
        <div className="m-actionbar m-only">
          <button className="btn btn-primary" onClick={() => openModal('newVault')}>
            <Icon name="plus" /> Nuova voce
          </button>
        </div>
      </DesktopOnly>
    </div>
  );
}
